import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader';
import loadable from '@loadable/component';

import RatingStars from 'core/components/RatingStars';
import { isAdmin, serialize, stripTags } from 'core/utils';

import DiscountLabel from './DiscountLabel';
import ListingBoxImage from './ListingBoxImage';
import ListingPrice from './ListingPrice';
import FavoriteHeart from 'core/components/FavoriteHeart';

import {
	ListingBox,
	ListingAdminLink,
	ProductInfo,
	ProductMods,
	ProductDetails,
	TitleLink,
	Description,
	StatusTag,
	StoreName,
	MarketingBadge,
} from './styles';
import { FlairTag } from 'core/styles';

const Gear = loadable(() => import('core/icons/Gear'));

const AdminGear = ({ versionId }) => {
	const url = `/admin/listings/editor/version/${versionId}`;
	return (
		<ListingAdminLink href={url} target="_blank" title={`This icon only appears to CJ Admins.`}>
			<Gear />
		</ListingAdminLink>
	);
};

class ListingCard extends Component {
	state = {
		isAdmin: false,
	}

	componentDidMount() {
		if (isAdmin()) {
			this.setState({ isAdmin: true });
		}
	}

	handleClick = () => {
		const { pt, gs } = this.props.queryParams;
		const { listing } = this.props;

		if (pt === 'search' && this.props.clickTrack) {
			this.props.clickTrack(gs, listing.id);
		}
	}

	render() {
		const { isAdmin } = this.state;
		const {
			listing,
			dealDiscount,
			dealDiscountType,
			dealDiscountAmount,
			queryParams,
			isLoggedIn,
			favorited,
			lazy,
			mobile,
			hideFav,
			hideDescription,
			priceValue,
			priceSort,
			compact,
		} = this.props;

		const storeReviewCount = listing.reviewCount + listing.otherReviews;
		const reviewRating = listing.storeRating && listing.storeRating > listing.avgRating ? listing.storeRating : listing.avgRating;
		const hasDiscount = listing.discountStr || dealDiscount;
		const hasMods = (listing.soldOut || hasDiscount);
		const queryString = queryParams ? `?${serialize(queryParams)}` : "";
		const href = `/subscription-box/${listing.slug}/${queryString}`;

		return (
			// Maintain the className & data-attrs for stat-tracking & `ie.less` purposes
			<ListingBox className="listing-box">
				<ProductInfo>
					<ListingBoxImage
						lazy={lazy}
						mobile={mobile}
						href={href}
						listingName={listing.name}
						listingImages={listing.listingImages}
						compact={compact}
					/>
					{/* Abs pos elements on borderline of img + details */}
					{/* keep adjacent + above ProductDetails */}
					{/* hasMods is to avoid rendering empty DOM node */}
					{hasMods && (
						<ProductMods>
							{listing.soldOut && (
								<StatusTag>Sold Out</StatusTag>
							)}
							{hasDiscount && (
								<DiscountLabel
									discountStr={listing.discountStr}
									discountType={listing.discountType}
									dealDiscount={dealDiscount}
									dealDiscountType={dealDiscountType}
									dealDiscountAmount={dealDiscountAmount}
									currencySymbol={listing.currencySymbol}
								/>
							)}
						</ProductMods>
					)}
					{/* main content */}
					<ProductDetails hasDiscount={hasDiscount}>
						<MarketingBadge>
							{Boolean(listing?.marketingBadges?.length) ? // eslint-disable-line no-extra-boolean-cast
								listing.marketingBadges[0].label.toUpperCase() :
								""
							}
						</MarketingBadge>
						<TitleLink
							href={href}
							data-slug={listing.slug}
							data-cj={'listingBox'}
							onClick={this.handleClick}>
							{stripTags(listing.name)}
						</TitleLink>
						{
							listing.storeName &&
							// Should we have that rule?
							listing.name !== listing.storeName &&
							<StoreName>by {stripTags(listing.storeName)}</StoreName>
						}
						{storeReviewCount > 0 ? (
							<RatingStars
								reviews={storeReviewCount}
								stars={reviewRating}
								tiny
								short
							/>
						) : null}
						{(!hideDescription && !mobile) && (
							<Description>{stripTags(listing.briefDescription)}</Description>
						)}
						<ListingPrice
							minPrice={listing.minUnitTermPrice}
							discountedPrice={listing.discountedPrice}
							discountType={listing.discountType}
							dealDiscount={dealDiscount}
							dealDiscountType={dealDiscountType}
							dealDiscountAmount={dealDiscountAmount}
							currencySymbol={listing.currencySymbol}
							termPrices={listing.termPrices}
							priceValue={priceValue}
							priceSort={priceSort}
						/>

						{listing.flairTags.map((flair) => (
							<FlairTag className={flair.tag} key={flair.tag}>
								{flair.label}
							</FlairTag>
						))}
					</ProductDetails>
				</ProductInfo>

				{/* Abs pos elements within listing-box */}
				{!hideFav && (
					<FavoriteHeart
						listingId={listing.listingId}
						isLoggedIn={isLoggedIn}
						favorited={favorited}
					/>
				)}
				{isAdmin && (<AdminGear versionId={listing.id} />)}
			</ListingBox>
		);
	}

	static defaultProps = {
		queryParams: null,
	};
}

export const listingCardProps = {
	listing: PropTypes.object.isRequired,
	dealDiscount: PropTypes.string,
	dealDiscountType: PropTypes.number,
	dealDiscountAmount: PropTypes.number,
	queryParams: PropTypes.object,
	isLoggedIn: PropTypes.bool,
	favorited: PropTypes.bool,
	lazy: PropTypes.bool,
	mobile: PropTypes.bool,
	hideFav: PropTypes.bool,
	hideDescription: PropTypes.bool,
	clickTrack: PropTypes.func,
	priceSort: PropTypes.string,
	priceValue: PropTypes.string,
	compact: PropTypes.bool,
};

ListingCard.propTypes = listingCardProps;

AdminGear.propTypes = {
	versionId: PropTypes.number,
};

export default hot(module)(ListingCard);
